// Cell-shape primitives — single SSOT for both structural-equality
// hashing (`cellKey`, used by every group-by / partition-by / distinct
// site) and human-readable rendering (`displayCell`, used wherever a
// cell becomes part of a label or output column name). Both functions
// share the type-dispatch ladder via `dispatchCell` so adding a new
// cell shape requires a single new branch.

import { Dataset, isUnitCell } from '../value.ts';

/**
 * Per-branch renderer for the cell-shape ladder. `array` and `object`
 * receive a `recurse` callback so a fold over nested cells stays
 * coherent with the outer renderer's choices (e.g. nested struct
 * cells render with the same SSOT key/display rules).
 */
type CellRenderer<ResultType> = {
  readonly nullish: () => ResultType;
  readonly string: (v: string) => ResultType;
  readonly number: (v: number) => ResultType;
  readonly boolean: (v: boolean) => ResultType;
  readonly date: (v: Date) => ResultType;
  readonly dataset: (v: Dataset) => ResultType;
  readonly array: (v: readonly unknown[], recurse: (item: unknown) => ResultType) => ResultType;
  readonly object: (v: Readonly<Record<string, unknown>>, recurse: (item: unknown) => ResultType) => ResultType;
  readonly other: (v: unknown) => ResultType;
};

/**
 * Single dispatch ladder over the cell shape universe. Owns the
 * "what kind of cell is this" classification — every cell-walking
 * function in this file builds on it. Adding a new cell shape means
 * one new branch here plus the corresponding callback on each
 * `CellRenderer`.
 */
const dispatchCell = <ResultType>(cell: unknown, r: CellRenderer<ResultType>): ResultType => {
  if (cell === null || cell === undefined) {
    return r.nullish();
  }
  if (typeof cell === 'string') {
    return r.string(cell);
  }
  if (typeof cell === 'number') {
    return r.number(cell);
  }
  if (isUnitCell(cell)) {
    // Unit-bearing cells key and render as their bare number — boxed 5 is
    // identical to plain 5 for group-by/distinct/join, and units display
    // at column level, never per cell. MUST stay above the object branch.
    return r.number(cell.value);
  }
  if (typeof cell === 'boolean') {
    return r.boolean(cell);
  }
  if (cell instanceof Date) {
    return r.date(cell);
  }
  if (cell instanceof Dataset) {
    return r.dataset(cell);
  }
  if (Array.isArray(cell)) {
    return r.array(cell, (x) => dispatchCell(x, r));
  }
  if (typeof cell === 'object') {
    return r.object(cell as Record<string, unknown>, (x) => dispatchCell(x, r));
  }
  return r.other(cell);
};

/**
 * Sorted `[key, value]` entries of an object. Both `cellKey` and
 * `displayCell` need deterministic key order for stable output;
 * this keeps the sort in one place.
 */
const sortedEntries = (o: Readonly<Record<string, unknown>>): readonly [string, unknown][] =>
  Object.entries(o).sort(([a], [b]) => a.localeCompare(b));

/**
 * Stable string identity for any column cell. Two cells produce the
 * same key iff they represent the same logical value.
 *
 *   - `null` / `undefined`              → `"_"` (single absent-value key)
 *   - `string` / `number` / `boolean`   → type-prefixed scalar
 *                                         (`"s:foo"`, `"n:1"`, `"b:true"`)
 *   - `Date`                            → `"d:<instant>"`
 *   - `Dataset`                          → `"ds"` (structural equality of
 *                                         nested datasets is neither cheap
 *                                         nor a sensible group-by key;
 *                                         collapse to one slot)
 *   - arrays                            → positionally canonicalised
 *   - plain objects                     → keys sorted, values recursively
 *                                         keyed (e.g. CDF instance refs
 *                                         `{ space, externalId }`)
 *
 * The type prefix means a number `1` never collides with the string
 * `"1"` even when a heterogeneously-typed Map mixes both; columns are
 * homogeneous in practice, but cross-column distinct-count stats see
 * mixed-type cells and the prefix keeps them disambiguated.
 */
export const cellKey = (cell: unknown): string =>
  dispatchCell(cell, {
    nullish: () => '_',
    string: (v) => `s:${v}`,
    number: (v) => `n:${v}`,
    boolean: (v) => `b:${v}`,
    date: (v) => `d:${v.getTime()}`,
    dataset: () => 'ds',
    array: (v, recurse) => `a:[${v.map(recurse).join(',')}]`,
    object: (v, recurse) =>
      `o:{${sortedEntries(v)
        .map(([k, val]) => `${k}=${recurse(val)}`)
        .join(',')}}`,
    other: (v) => `?:${String(v)}`,
  });
/**
 * Human-readable string rendering of a cell. Paired with {@link cellKey}:
 *
 *   - `cellKey(cell)`   — structural identity (Map keys, distinct
 *                         counts, group-by buckets)
 *   - `displayCell(cell)` — what the user / LLM should see (output
 *                           column names, distinct-value samples,
 *                           anywhere a cell becomes part of a label)
 *
 * Keeping the two functions distinct prevents the conflation that
 * makes `String(cell)` produce `"[object Object]"` whenever the
 * codebase needs both a stable identity AND a readable label for the
 * same cell.
 *
 *   - `null` / `undefined`              → `""`
 *   - `string`                          → cell verbatim
 *   - `number` / `boolean`              → `String(cell)`
 *   - `Date`                            → ISO 8601
 *   - `Dataset`                          → `"<dataset>"`
 *   - arrays                            → `"[a,b,c]"`
 *   - plain objects                     → `"{k:v,k:v}"` (keys sorted)
 */
export const displayCell = (cell: unknown): string =>
  dispatchCell(cell, {
    nullish: () => '',
    string: (v) => v,
    number: String,
    boolean: String,
    date: (v) => v.toISOString(),
    dataset: () => '<dataset>',
    array: (v, recurse) => `[${v.map(recurse).join(',')}]`,
    object: (v, recurse) =>
      `{${sortedEntries(v)
        .map(([k, val]) => `${k}:${recurse(val)}`)
        .join(',')}}`,
    other: String,
  });
